import { Settings2 } from 'lucide-react';
import { PassbookQr } from '@/shared/components/passbook-qr';
import { usePassbook } from '@/shared/lib/passbook';
import { quantityLabel } from '@/shared/lib/passbook-utils';
import { PageHeader } from '@/shared/components/page-header';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Badge } from '@/shared/ui/badge';

function Flag({ label, on }: { label: string; on?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-border/50 px-3 py-2 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <Badge variant={on ? 'success' : 'secondary'}>{on ? 'Ativo' : 'Off'}</Badge>
    </div>
  );
}

/** Configurações da loja — info do programa e validade (legado configuracoes SPA). */
export function StoreSettingsPage() {
  const { passbook } = usePassbook();
  const label = quantityLabel(passbook);

  const expireMode = passbook.hasExpirationDate
    ? 'Data fixa'
    : passbook.hasExpire || passbook.hasExpireStamps
      ? 'Por dias'
      : 'Sem expiração';

  return (
    <>
      <PageHeader
        title="Configurações"
        description={`${passbook.organizationName} — parâmetros do programa`}
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Settings2 className="size-4" /> Programa
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex justify-between gap-3 border-b border-border/40 py-2">
              <span className="text-muted-foreground">Nome</span>
              <span className="text-right font-medium">{passbook.organizationName}</span>
            </div>
            <div className="flex justify-between gap-3 border-b border-border/40 py-2">
              <span className="text-muted-foreground">Modelo</span>
              <span className="text-right font-medium">{passbook.model ?? '—'}</span>
            </div>
            <div className="flex justify-between gap-3 border-b border-border/40 py-2">
              <span className="text-muted-foreground">Unidade</span>
              <span className="text-right font-medium">{label}</span>
            </div>
            <div className="flex justify-between gap-3 border-b border-border/40 py-2">
              <span className="text-muted-foreground">Identificador</span>
              <span className="text-right font-medium">{passbook.fieldUniqueIdLabel ?? 'CPF'}</span>
            </div>
            {passbook.shortUrl ? (
              <div className="flex justify-center py-3">
                <PassbookQr idPassbook={String(passbook._id)} url={passbook.shortUrl} name={`qr-${passbook.slug ?? passbook._id}`} />
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Validade de {label.toLowerCase()}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex justify-between gap-3 border-b border-border/40 py-2">
              <span className="text-muted-foreground">Modo</span>
              <span className="font-medium">{expireMode}</span>
            </div>
            {(passbook.hasExpire || passbook.hasExpireStamps) && passbook.expireDays != null ? (
              <div className="flex justify-between gap-3 border-b border-border/40 py-2">
                <span className="text-muted-foreground">Dias para expirar</span>
                <span className="font-medium">{passbook.expireDays}</span>
              </div>
            ) : null}
            {passbook.hasExpirationDate && passbook.expirationDate ? (
              <div className="flex justify-between gap-3 border-b border-border/40 py-2">
                <span className="text-muted-foreground">Data de expiração</span>
                <span className="font-medium">{passbook.expirationDate}</span>
              </div>
            ) : null}
            <p className="pt-1 text-xs text-muted-foreground">
              A edição avançada de validade, promoções e crédito por CPF permanece no painel legado
              enquanto a API de escrita dessas regras não for exposta neste front.
            </p>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Recursos do programa</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            <Flag label="Pontos" on={passbook.hasPoints} />
            <Flag label="Cashback" on={passbook.hasCashback} />
            <Flag label="Multi-loja" on={passbook.hasMultiStore} />
            <Flag label="Produtos" on={passbook.hasProducts} />
            <Flag label="Campanhas" on={passbook.hasCampaign !== false} />
            <Flag label="Campanhas avançadas" on={passbook.hasCampaignAdvanced} />
            <Flag
              label="WhatsApp"
              on={passbook.hasSimpleCampaignWhatsApp || passbook.hasCampaignWhatsapp}
            />
            <Flag label="Expiração por dias" on={passbook.hasExpire || passbook.hasExpireStamps} />
            <Flag
              label="Campos customizados"
              on={Boolean(passbook.addFields?.length) && passbook.showAddFields !== false}
            />
            <Flag label="Tiers de fidelidade" on={passbook.hasTiers} />
            <Flag label="Cupons" on={passbook.hasCoupons} />
          </CardContent>
        </Card>
      </div>
    </>
  );
}
